/**
 * 旅行回忆系统 - 旅行编辑页
 * 修改行程、描述、小作文、确幸语录等
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { travelApi } from '../services/travelMemoriesApi';
import './TravelMemoriesTravelEdit.css';

const TravelMemoriesTravelEdit = () => {
  const { spaceId, travelId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    startDate: '',
    endDate: '',
    locations: [],
    description: '',
    essayFullText: '',
    journey: [],
    moments: []
  });

  useEffect(() => {
    if (spaceId && travelId) loadTravel();
  }, [spaceId, travelId]);

  const loadTravel = async () => {
    try {
      setLoading(true);
      const res = await travelApi.getTravelById(spaceId, travelId);
      if (res && res.success && res.data) {
        const t = res.data;
        setForm({
          title: t.title || '',
          subtitle: t.subtitle || '',
          startDate: t.startDate || '',
          endDate: t.endDate || '',
          locations: Array.isArray(t.locations) ? [...t.locations] : [],
          description: t.description || '',
          essayFullText: t.essayFullText || '',
          journey: Array.isArray(t.journey) && t.journey.length > 0
            ? t.journey.map((j) => ({
                day: j.day || '',
                location: j.location || '',
                activity: j.activity || '',
                coordinates: j.coordinates || { lat: 0, lng: 0 },
                moments: Array.isArray(j.moments) ? j.moments.map((m) => ({
                  id: m.id || `dm-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  quote: m.quote || '',
                  author: m.author || '',
                  color: m.color || '#FFB6A3'
                })) : []
              }))
            : [{ day: '', location: '', activity: '', coordinates: { lat: 0, lng: 0 }, moments: [] }],
          moments: Array.isArray(t.moments) && t.moments.length > 0
            ? t.moments.map((m) => ({
                id: m.id || `m-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                quote: m.quote || '',
                author: m.author || '',
                color: m.color || '#FFB6A3'
              }))
            : []
        });
      }
    } catch (e) {
      console.error('加载旅行失败', e);
    } finally {
      setLoading(false);
    }
  };

  const update = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const updateJourney = (index, field, value) => {
    setForm((f) => {
      const j = [...(f.journey || [])];
      if (!j[index]) j[index] = { day: '', location: '', activity: '', coordinates: { lat: 0, lng: 0 } };
      j[index] = { ...j[index], [field]: value };
      return { ...f, journey: j };
    });
  };

  const addJourneyDay = () => {
    setForm((f) => ({
      ...f,
      journey: [...(f.journey || []), { day: '', location: '', activity: '', coordinates: { lat: 0, lng: 0 }, moments: [] }]
    }));
  };

  const updateJourneyDayMoment = (journeyIndex, momentIndex, field, value) => {
    setForm((f) => {
      const j = [...(f.journey || [])];
      if (!j[journeyIndex]) return f;
      const moments = [...(j[journeyIndex].moments || [])];
      if (!moments[momentIndex]) moments[momentIndex] = { id: `dm-${Date.now()}`, quote: '', author: '', color: '#FFB6A3' };
      moments[momentIndex] = { ...moments[momentIndex], [field]: value };
      j[journeyIndex] = { ...j[journeyIndex], moments };
      return { ...f, journey: j };
    });
  };

  const addJourneyDayMoment = (journeyIndex) => {
    setForm((f) => {
      const j = [...(f.journey || [])];
      if (!j[journeyIndex]) return f;
      const moments = [...(j[journeyIndex].moments || []), { id: `dm-${Date.now()}`, quote: '', author: '', color: '#FFB6A3' }];
      j[journeyIndex] = { ...j[journeyIndex], moments };
      return { ...f, journey: j };
    });
  };

  const removeJourneyDayMoment = (journeyIndex, momentIndex) => {
    setForm((f) => {
      const j = [...(f.journey || [])];
      if (!j[journeyIndex] || !j[journeyIndex].moments) return f;
      j[journeyIndex] = {
        ...j[journeyIndex],
        moments: j[journeyIndex].moments.filter((_, i) => i !== momentIndex)
      };
      return { ...f, journey: j };
    });
  };

  const removeJourneyDay = (index) => {
    setForm((f) => ({
      ...f,
      journey: f.journey.filter((_, i) => i !== index)
    }));
  };

  const updateMoment = (index, field, value) => {
    setForm((f) => {
      const m = [...(f.moments || [])];
      if (!m[index]) m[index] = { id: `m-${Date.now()}`, quote: '', author: '', color: '#FFB6A3' };
      m[index] = { ...m[index], [field]: value };
      return { ...f, moments: m };
    });
  };

  const addMoment = () => {
    setForm((f) => ({
      ...f,
      moments: [...(f.moments || []), { id: `m-${Date.now()}`, quote: '', author: '', color: '#FFB6A3' }]
    }));
  };

  const removeMoment = (index) => {
    setForm((f) => ({ ...f, moments: f.moments.filter((_, i) => i !== index) }));
  };

  const locationsStr = form.locations.join('、');
  const setLocationsStr = (str) => {
    const arr = str.split(/[、,，\n]/).map((s) => s.trim()).filter(Boolean);
    update('locations', arr);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      alert('请填写旅行标题');
      return;
    }
    try {
      setSaving(true);
      await travelApi.updateTravel(spaceId, travelId, {
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        startDate: form.startDate.trim() || undefined,
        endDate: form.endDate.trim() || undefined,
        locations: form.locations,
        description: form.description.trim() || '',
        essayFullText: form.essayFullText.trim() || '',
        journey: form.journey.filter((j) => j.day || j.location || j.activity).map((j) => ({
          day: j.day,
          location: j.location,
          activity: j.activity,
          coordinates: j.coordinates || { lat: 0, lng: 0 },
          moments: (j.moments || []).filter((m) => m.quote || m.author).map((m) => ({
            id: m.id,
            quote: m.quote,
            author: m.author,
            color: m.color || '#FFB6A3'
          }))
        })),
        moments: form.moments.filter((m) => m.quote || m.author).map((m) => ({
          id: m.id,
          quote: m.quote,
          author: m.author,
          color: m.color
        }))
      });
      navigate(`/travel-memories/spaces/${spaceId}/travels/${travelId}`);
    } catch (err) {
      console.error('保存失败', err);
      alert('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="tm-edit-loading">
        <div>加载中...</div>
      </div>
    );
  }

  return (
    <div className="tm-edit-page">
      <div className="tm-edit-header">
        <button type="button" className="tm-edit-back" onClick={() => navigate(-1)}>
          ← 返回
        </button>
        <h1 className="tm-edit-title">编辑行程</h1>
      </div>

      <form className="tm-edit-form" onSubmit={handleSubmit}>
        <section className="tm-edit-section">
          <h2>基本信息</h2>
          <label>旅行标题</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="如：25-10 广西之旅"
          />
          <label>副标题</label>
          <input
            type="text"
            value={form.subtitle}
            onChange={(e) => update('subtitle', e.target.value)}
            placeholder="如：2025年10月・崇左、明仕田园、梧州"
          />
          <div className="tm-edit-row">
            <div>
              <label>开始日期</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => update('startDate', e.target.value)}
              />
            </div>
            <div>
              <label>结束日期</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => update('endDate', e.target.value)}
              />
            </div>
          </div>
          <label>地点（用顿号、逗号或换行分隔）</label>
          <textarea
            value={locationsStr}
            onChange={(e) => setLocationsStr(e.target.value)}
            placeholder="崇左、明仕田园、梧州"
            rows={2}
          />
        </section>

        <section className="tm-edit-section">
          <h2>旅行描述</h2>
          <textarea
            value={form.description}
            onChange={(e) => update('description', e.target.value)}
            placeholder="简短描述这次旅行"
            rows={4}
          />
        </section>

        <section className="tm-edit-section">
          <h2>小作文全文（原文）</h2>
          <textarea
            value={form.essayFullText}
            onChange={(e) => update('essayFullText', e.target.value)}
            placeholder="从 docx 导入的完整小作文，可在此修改"
            rows={8}
            className="tm-edit-essay"
          />
        </section>

        <section className="tm-edit-section">
          <h2>路线行程（按天）</h2>
          <p className="tm-edit-hint">每一天：日期、地点、当天活动/小作文摘要</p>
          {(form.journey || []).map((j, i) => (
            <div key={i} className="tm-edit-journey-item">
              <div className="tm-edit-journey-row">
                <input
                  type="text"
                  placeholder="日期 如 2025-10-04"
                  value={j.day}
                  onChange={(e) => updateJourney(i, 'day', e.target.value)}
                />
                <input
                  type="text"
                  placeholder="地点"
                  value={j.location}
                  onChange={(e) => updateJourney(i, 'location', e.target.value)}
                />
                <button type="button" className="tm-edit-remove" onClick={() => removeJourneyDay(i)}>
                  删除
                </button>
              </div>
              <textarea
                placeholder="当天活动 / 小作文内容"
                value={j.activity}
                onChange={(e) => updateJourney(i, 'activity', e.target.value)}
                rows={2}
              />
              <div className="tm-edit-journey-day-moments">
                <span className="tm-edit-journey-day-moments-label">当日对话/确幸</span>
                {(j.moments || []).map((m, mi) => (
                  <div key={m.id || mi} className="tm-edit-moment-item">
                    <input
                      type="text"
                      placeholder="语录"
                      value={m.quote}
                      onChange={(e) => updateJourneyDayMoment(i, mi, 'quote', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="作者"
                      value={m.author}
                      onChange={(e) => updateJourneyDayMoment(i, mi, 'author', e.target.value)}
                    />
                    <input
                      type="text"
                      placeholder="颜色"
                      value={m.color}
                      onChange={(e) => updateJourneyDayMoment(i, mi, 'color', e.target.value)}
                      className="tm-edit-color"
                    />
                    <button type="button" className="tm-edit-remove" onClick={() => removeJourneyDayMoment(i, mi)}>
                      删
                    </button>
                  </div>
                ))}
                <button type="button" className="tm-edit-add-sm" onClick={() => addJourneyDayMoment(i)}>
                  + 添加一条
                </button>
              </div>
            </div>
          ))}
          <button type="button" className="tm-edit-add" onClick={addJourneyDay}>
            + 添加一天
          </button>
        </section>

        <section className="tm-edit-section">
          <h2>确幸语录</h2>
          {(form.moments || []).map((m, i) => (
            <div key={m.id || i} className="tm-edit-moment-item">
              <input
                type="text"
                placeholder="语录"
                value={m.quote}
                onChange={(e) => updateMoment(i, 'quote', e.target.value)}
              />
              <input
                type="text"
                placeholder="作者"
                value={m.author}
                onChange={(e) => updateMoment(i, 'author', e.target.value)}
              />
              <input
                type="text"
                placeholder="颜色 #FFB6A3"
                value={m.color}
                onChange={(e) => updateMoment(i, 'color', e.target.value)}
                className="tm-edit-color"
              />
              <button type="button" className="tm-edit-remove" onClick={() => removeMoment(i)}>
                删除
              </button>
            </div>
          ))}
          <button type="button" className="tm-edit-add" onClick={addMoment}>
            + 添加一条语录
          </button>
        </section>

        <div className="tm-edit-actions">
          <button type="button" className="tm-edit-cancel" onClick={() => navigate(-1)}>
            取消
          </button>
          <button type="submit" className="tm-edit-submit" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TravelMemoriesTravelEdit;
